import { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Tabs } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import { useAdminAccessTabVisibility } from "../../../features/access/api";

type AdminTabValue =
  | "users"
  | "access"
  | "complexes"
  | "structures"
  | "license-inventory"
  | "license-assignments"
  | "permissions";

const ADMIN_BASE_PATH = "/admin";

const getActiveTab = (pathname: string): AdminTabValue => {
  const segment = pathname.replace(`${ADMIN_BASE_PATH}/`, "").split("/")[0];
  if (
    segment === "users" ||
    segment === "access" ||
    segment === "complexes" ||
    segment === "structures" ||
    segment === "license-inventory" ||
    segment === "license-assignments" ||
    segment === "permissions"
  ) {
    return segment;
  }
  return "users";
};

export const AdminLayout = () => {
  const { translate } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = getActiveTab(location.pathname);

  // Visibilidade da aba "Acessos": só aparece para owner | ownerViewer.
  // Abas de licença (Inventário / Alocações): ocultas para complexAdmin tellus.
  // Ver useAdminAccessTabVisibility para as regras completas.
  const { canSeeAccessTab, canSeeLicenseTabs } = useAdminAccessTabVisibility();

  const tabs = useMemo(
    () => [
      {
        value: "users" as const,
        label: translate("admin.tabs.users"),
        content: <></>,
      },
      {
        value: "access" as const,
        label: translate("admin.tabs.access"),
        content: <></>,
        hidden: !canSeeAccessTab,
      },
      {
        value: "complexes" as const,
        label: translate("admin.tabs.complexes"),
        content: <></>,
      },
      {
        value: "structures" as const,
        label: translate("admin.tabs.structures"),
        content: <></>,
      },
      {
        value: "license-inventory" as const,
        label: translate("admin.tabs.licenseInventory"),
        content: <></>,
        hidden: !canSeeLicenseTabs,
      },
      {
        value: "license-assignments" as const,
        label: translate("admin.tabs.licenseAssignments"),
        content: <></>,
        hidden: !canSeeLicenseTabs,
      },
      {
        value: "permissions" as const,
        label: translate("admin.tabs.permissions"),
        content: <></>,
      },
    ],
    [translate, canSeeAccessTab, canSeeLicenseTabs],
  );

  const handleChange = (value?: string) => {
    if (!value || value === activeTab) return;
    navigate(`${ADMIN_BASE_PATH}/${value}`);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="admin-tabs">
        <Tabs
          key={activeTab}
          tabs={tabs}
          defaultValue={activeTab}
          onChange={handleChange}
        />
      </div>
      <Outlet />
    </div>
  );
};

export default AdminLayout;
